import {
  convertAppNodeToCanvasTokenNode,
  type GroupMemberCounts,
  getGroupLabel,
  getGroupThumbnailUrl,
  getNodeLabel,
  getNodeThumbnailUrl,
  type MentionItem,
  useCanvasTokenNodeStore,
  useChatComposerStore,
  useChatSidebarStore,
} from '@air/feature-ag-ui-chat';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useEditCanvasStore } from '../store';
import { getGroupMembers } from './group/nodeGrouping';

const MENTION_THUMBNAIL_SIZE = 32;

export type OpenChatForCanvasNodePromptOptions = {
  /**
   * When true, always mention the node itself even if it belongs to a group.
   * Used when a single layer is drilled into within a group, where the prompt
   * should target that layer rather than the whole group.
   */
  individual?: boolean;
};

/** Resolves the @-mention item for a canvas node — its group when it belongs to one, else the node itself. */
const buildCanvasNodeMentionItem = (
  nodeId: string,
  options?: OpenChatForCanvasNodePromptOptions,
): MentionItem | null => {
  const appNodes = useEditCanvasStore.getState().nodes;
  const appNode = appNodes.find((n) => n.id === nodeId);

  if (!appNode) {
    return null;
  }

  const canvasTokenNodes = useCanvasTokenNodeStore.getState().nodes;
  const groupId = options?.individual ? undefined : appNode.groupId;

  // A grouped node mentions the whole group unless the caller asked for the individual layer.
  let item: MentionItem | null = null;

  if (groupId) {
    const members = getGroupMembers(appNodes, groupId);

    if (members.length > 1) {
      const memberTokenNodes = members
        .map((m) => {
          const key = m.remoteId ?? m.id;

          return canvasTokenNodes[key] ?? convertAppNodeToCanvasTokenNode(m);
        })
        .filter((n): n is NonNullable<typeof n> => n != null);

      if (memberTokenNodes.length > 0) {
        const counts: GroupMemberCounts = { image: 0, text: 0, video: 0 };
        for (const n of memberTokenNodes) {
          counts[n.type]++;
        }
        item = {
          id: groupId,
          memberCount: members.length,
          name: getGroupLabel(counts),
          thumbnailUrl: getGroupThumbnailUrl(memberTokenNodes, MENTION_THUMBNAIL_SIZE),
          type: 'group',
        };
      }
    }
  }

  if (!item) {
    const key = appNode.remoteId ?? appNode.id;
    const node = canvasTokenNodes[key] ?? convertAppNodeToCanvasTokenNode(appNode);

    if (!node) {
      return null;
    }
    item = {
      id: key,
      name: getNodeLabel(node),
      thumbnailUrl: getNodeThumbnailUrl(node, MENTION_THUMBNAIL_SIZE),
      type: node.type,
      ...(node.updatedAt && { updatedAt: node.updatedAt }),
    };
  }

  return item;
};

/** Opens the chat sidebar. Split from mention staging so callers can await the composer between. */
export const openChatForCanvasPrompt = () => {
  useChatSidebarStore.getState().setOpen(true);
};

/**
 * Stages an @-mention per node, in order; `nodeIds[0]` is the primary. Multi-mention callers must
 * await `waitForComposerReady` first — the pre-mount fallback below holds only one item.
 */
export const stageCanvasNodesPromptMentions = (
  nodeIds: readonly string[],
  options?: OpenChatForCanvasNodePromptOptions,
) => {
  const [primaryNodeId] = nodeIds;

  if (!primaryNodeId) {
    return;
  }

  // Built separately from the rest: `items` is filtered, so items[0] would be the first SURVIVING
  // mention, silently promoting a companion to primary if the top node failed to resolve.
  const primaryItem = buildCanvasNodeMentionItem(primaryNodeId, options);

  if (!primaryItem) {
    return;
  }

  const items = [
    primaryItem,
    ...nodeIds
      .slice(1)
      .map((nodeId) => buildCanvasNodeMentionItem(nodeId, options))
      .filter((item): item is MentionItem => item !== null),
  ];

  const primaryAppNode = useEditCanvasStore.getState().nodes.find((n) => n.id === primaryNodeId);

  // Single read of the store, single dispatch point
  const { insertToken, setPendingToken, setPromptSourceNodeId } = useChatComposerStore.getState();

  // When prompting a single layer inside a group, mark it as the in-place edit
  // target so the result replaces that layer (stays in the group) instead of
  // appending. Keyed by the mention id (remoteId ?? id) so the send can verify
  // the layer is still referenced in the message. Cleared for whole-group /
  // standalone prompts.
  setPromptSourceNodeId(
    options?.individual && primaryAppNode?.groupId ? (primaryAppNode.remoteId ?? primaryAppNode.id) : null,
  );

  if (insertToken) {
    for (const item of items) {
      insertToken(item);
    }

    return;
  }

  // Composer not mounted yet: pre-mount staging holds a single item, so only the primary
  // mention can be queued. Multi-mention callers await waitForComposerReady first, so they
  // take the insertToken branch above and never lose their extra mentions here.
  if (nodeIds.length > 1) {
    // Reaching here with multiple nodes means a caller skipped waitForComposerReady — the mentions
    // past the first are being dropped silently, which is the regression this reports rather than hides.
    reportErrorToBugsnag({
      context: 'stageCanvasNodesPromptMentions: extra mentions truncated because the composer was not mounted',
      error: new Error('Canvas prompt mentions truncated before composer mount'),
      metadata: { itemCount: items.length, nodeCount: nodeIds.length },
    });
  }

  setPendingToken(primaryItem);
};

/**
 * Opens chat and stages an @-mention per node, synchronously. A not-yet-mounted composer keeps only
 * the primary mention; callers that need all of them must await the composer between the two steps,
 * as `openChatForCanvasNodeOutcome` does.
 */
export const openChatForCanvasNodesPrompt = (
  nodeIds: readonly string[],
  options?: OpenChatForCanvasNodePromptOptions,
) => {
  openChatForCanvasPrompt();
  stageCanvasNodesPromptMentions(nodeIds, options);
};

/** Single-node convenience wrapper around {@link openChatForCanvasNodesPrompt}. */
export const openChatForCanvasNodePrompt = (nodeId: string, options?: OpenChatForCanvasNodePromptOptions) =>
  openChatForCanvasNodesPrompt([nodeId], options);
